import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Spinner } from '@heroui/react';
import { countParam } from '@/pages/catalogImport/notices';
import { cn } from '@/utils/cnUtils';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';

type ActionBarStatusProps = {
    /** The line's id: on a phone it describes Exclude all. */
    id: string;
    isImporting: boolean;
    isChecking: boolean;
    /** Rows that need attention and are not excluded. */
    needsAttentionCount: number;
    /** "Importing N rows…", which also names the bar's progress bar. */
    importingMessage: string;
};

/**
 * The action bar's message, a polite live region: the import is running
 * ("Importing N rows…"), a check is (with a spinner), how many rows need
 * attention (in danger tone), or that none does.
 */
export const ActionBarStatus: React.FC<ActionBarStatusProps> = ({
    id,
    isImporting,
    isChecking,
    needsAttentionCount,
    importingMessage,
}) => {
    const { t } = useTranslation('pages');
    const isBusy = isImporting || isChecking;
    const needsAttention = needsAttentionCount > 0;

    return (
        <div
            aria-live="polite"
            className={cn(
                'flex items-center gap-2 text-sm font-medium sm:shrink-0 sm:whitespace-nowrap',
                !isBusy && needsAttention && 'text-danger',
            )}
            id={id}
            role="status"
            {...getNodeTestIdProp('catalog-import-action-bar-status')}
        >
            {/* The spinner is its own status region; the line's text
                already says a check is running. The import's progress
                bar is under the line, not in it. */}
            {isImporting ? null : isChecking ? (
                <Spinner aria-hidden color="current" size="sm" />
            ) : (
                <Icon
                    aria-hidden
                    className={cn(
                        'size-4 shrink-0',
                        !needsAttention && 'text-success',
                    )}
                    icon={
                        needsAttention
                            ? 'gravity-ui:circle-exclamation'
                            : 'gravity-ui:circle-check'
                    }
                />
            )}
            <span>
                {isImporting
                    ? importingMessage
                    : isChecking
                      ? t('catalogImport.products.checking')
                      : needsAttention
                        ? t('catalogImport.products.bar.needsAttention', {
                              count: needsAttentionCount,
                              rows: countParam(needsAttentionCount),
                          })
                        : t('catalogImport.products.bar.allClear')}
            </span>
        </div>
    );
};
